import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AuthUser, ForgotPasswordRequest, LoginRequest, ResetPasswordRequest } from "@edlab-fms/shared";
import { ApiClientError, api } from "./client";

export const authQueryKey = ["auth", "me"] as const;

export function useAuthQuery() {
  return useQuery({
    queryKey: authQueryKey,
    queryFn: async (): Promise<AuthUser | null> => {
      try {
        const response = await api.get<{ user: AuthUser }>("/auth/me");
        return response.user;
      } catch (error) {
        if (error instanceof ApiClientError && error.status === 401) {
          return null;
        }
        throw error;
      }
    },
    retry: false,
    staleTime: 60_000,
  });
}

export function useLoginMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: LoginRequest) =>
      api.post<{ user: AuthUser }>("/auth/login", body),
    onSuccess: (data) => {
      queryClient.setQueryData(authQueryKey, data.user);
    },
  });
}

export function useLogoutMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.post<{ success: boolean }>("/auth/logout"),
    onSuccess: () => {
      queryClient.setQueryData(authQueryKey, null);
    },
  });
}

export function useForgotPasswordMutation() {
  return useMutation({
    mutationFn: (body: ForgotPasswordRequest) =>
      api.post<{ message: string }>("/auth/forgot-password", body),
  });
}

export function useResetPasswordMutation() {
  return useMutation({
    mutationFn: (body: ResetPasswordRequest) =>
      api.post<{ success: boolean }>("/auth/reset-password", body),
  });
}
